import type { InkVerdict, InkTrackState } from "@/components/visual/ink";
import type { InkAgingBand, InkIssuePoint } from "@/components/visual/ink-report";
import { addDays, diffDays, isISODate, type ISODate } from "@/engine/date";
import type { ProjectRecord, RecordSeverity } from "@/engine/types";
import { formatDate } from "@/lib/date-format";
import { issueAging, type IssueRow } from "@/features/weekly-report/weekly-report-model";
import { isRecordOverdue, recordPhase } from "@/store/record-ops";
import { weekStartMonday, type LoadMark } from "./person-load";

/**
 * 멤버 프로필의 이슈·작업·파일.
 * 화면은 참여 플랜, 배정된 작업, 활동 이력, 파일로 그린다.
 */

export interface PersonIssueSource {
  id: string;
  name: string;
  lifecycle?: string;
  records?: ProjectRecord[];
}

export interface PersonIssueTrack {
  id: string;
  projectId: string;
  title: string;
  opened: ISODate;
  state: InkTrackState;
  extent: number;
  comment?: string;
  attachments?: string[];
}

export interface PersonIssueBoard {
  tracks: PersonIssueTrack[];
  points: InkIssuePoint[];
  bands: InkAgingBand[];
  openCount: number;
  lateCount: number;
}

const EMPTY_BOARD: PersonIssueBoard = { tracks: [], points: [], bands: [], openCount: 0, lateCount: 0 };
const WEEKS = 6;
const DONE_LIMIT = 1;

interface HeldIssue {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  opened: ISODate;
  due?: ISODate;
  closed?: ISODate;
  open: boolean;
  late: boolean;
  severity?: RecordSeverity;
  comment?: string;
  attachments?: string[];
}

export function personIssueBoard(sources: readonly PersonIssueSource[], name: string, resourceId: string, today: ISODate): PersonIssueBoard {
  const issues = heldIssues(sources, name, resourceId, today);
  if (issues.length === 0) return EMPTY_BOARD;
  const open = issues.filter((issue) => issue.open);
  const multi = new Set(issues.map((issue) => issue.projectId)).size > 1;
  const done = issues
    .filter((issue) => !issue.open)
    .sort((a, b) => (b.closed ?? "").localeCompare(a.closed ?? ""))
    .slice(0, DONE_LIMIT);
  const tracks = [...open].sort(byUrgency).concat(done).map((issue) => toTrack(issue, today, multi));
  return {
    tracks,
    points: issuePoints(issues, today),
    bands: issueAging(open.map((issue) => agingRow(issue, name, today))),
    openCount: open.length,
    lateCount: open.filter((issue) => issue.late).length,
  };
}

export function taskVerdict(mark: LoadMark, today: ISODate): InkVerdict | null {
  if (mark.done) return null;
  if (mark.status === "blocked") return "blocked";
  if (!mark.date) return null;
  const thisStart = weekStartMonday(today);
  const thisEnd = addDays(thisStart, 6);
  if (mark.date >= thisStart && mark.date <= thisEnd) return "thisWeek";
  if (mark.date > thisEnd && mark.date <= addDays(thisEnd, 7)) return "nextWeek";
  return null;
}

export function taskExtent(mark: LoadMark): number {
  if (typeof mark.percent === "number" && Number.isFinite(mark.percent)) {
    return Math.max(0, Math.min(100, Math.round(mark.percent)));
  }
  if (mark.status === "inProgress") return 55;
  if (mark.status === "blocked") return 20;
  return 8;
}

const VERDICT_RANK: Record<InkVerdict, number> = { blocked: 0, thisWeek: 1, nextWeek: 2 };

export function judgmentMarks(marks: readonly LoadMark[], today: ISODate): LoadMark[] {
  return marks
    .filter((mark) => taskVerdict(mark, today) != null)
    .sort((a, b) => {
      const rank = VERDICT_RANK[taskVerdict(a, today)!] - VERDICT_RANK[taskVerdict(b, today)!];
      if (rank !== 0) return rank;
      return (a.date ?? "9999-12-31").localeCompare(b.date ?? "9999-12-31");
    });
}

export function remainingByDue(marks: readonly LoadMark[]): LoadMark[] {
  return marks
    .filter((mark) => !mark.done)
    .sort((a, b) => {
      if (!a.date && !b.date) return a.title.localeCompare(b.title, "ko");
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.localeCompare(b.date) || a.title.localeCompare(b.title, "ko");
    });
}

export function filesForPerson<T extends { addedBy: string; ownerKind: string; ownerId: string }>(
  entries: readonly T[],
  name: string,
  taskIds: ReadonlySet<string>,
): T[] {
  return entries.filter((entry) => (name !== "" && entry.addedBy === name) || (entry.ownerKind === "task" && taskIds.has(entry.ownerId)));
}

export function dueWithin(marks: readonly LoadMark[], today: ISODate, days: number): number {
  const end = addDays(today, days);
  return marks.filter((mark) => !mark.done && !!mark.date && mark.date <= end).length;
}

function heldIssues(sources: readonly PersonIssueSource[], name: string, resourceId: string, today: ISODate): HeldIssue[] {
  if (!name && !resourceId) return [];
  const issues: HeldIssue[] = [];
  for (const source of sources) {
    if (source.lifecycle === "archived" || source.lifecycle === "trashed") continue;
    for (const record of source.records ?? []) {
      if (record.kind !== "issue" || !isMine(record, name, resourceId)) continue;
      const opened = dayOf(record.createdAt);
      if (!opened) continue;
      const open = recordPhase(record.status) !== "closed";
      issues.push({
        id: record.id,
        projectId: source.id,
        projectName: source.name,
        title: record.title || "제목 없음",
        opened,
        due: dayOf(record.due),
        closed: open ? undefined : dayOf(record.closedAt) ?? dayOf(record.updatedAt),
        open,
        late: isRecordOverdue(record, today),
        severity: record.severity,
        comment: open ? undefined : closeNote(record),
        attachments: attachmentNames(record),
      });
    }
  }
  return issues;
}

function isMine(record: ProjectRecord, name: string, resourceId: string): boolean {
  return record.assignees.some((id) => id === name || (resourceId !== "" && id === resourceId));
}

function issuePoints(issues: readonly HeldIssue[], today: ISODate): InkIssuePoint[] {
  const start = addDays(weekStartMonday(today), -7 * (WEEKS - 1));
  const points: InkIssuePoint[] = [];
  for (let index = 0; index < WEEKS; index += 1) {
    const week = addDays(start, index * 7);
    const cutoff = asOf(week, today);
    let opened = 0;
    let closed = 0;
    for (const issue of issues) {
      if (issue.opened <= cutoff) opened += 1;
      if (issue.closed && issue.closed <= cutoff) closed += 1;
    }
    points.push({
      weekId: week,
      label: formatDate(week, "monthDay"),
      start: week,
      opened,
      closed,
      open: Math.max(0, opened - closed),
    });
  }
  return points;
}

function agingRow(issue: HeldIssue, name: string, today: ISODate): IssueRow {
  const severity = issue.severity === "high" || issue.severity === "low" ? issue.severity : "medium";
  return {
    id: issue.id,
    title: issue.title,
    severity,
    ownerId: name,
    opened: issue.opened,
    due: issue.due,
    ageDays: Math.max(0, diffDays(issue.opened, today)),
    state: issue.late ? "late" : "open",
    escalated: false,
  };
}

function toTrack(issue: HeldIssue, today: ISODate, multi: boolean): PersonIssueTrack {
  const state: InkTrackState = issue.open ? (issue.late ? "late" : "open") : "done";
  return {
    id: issue.id,
    projectId: issue.projectId,
    title: multi ? `${issue.projectName} · ${issue.title}` : issue.title,
    opened: issue.opened,
    state,
    extent: state === "done" ? 100 : trackExtent(issue, today),
    comment: state === "done" ? issue.comment : undefined,
    attachments: issue.attachments,
  };
}

function byUrgency(a: HeldIssue, b: HeldIssue): number {
  const late = Number(b.late) - Number(a.late);
  if (late !== 0) return late;
  const severity = severityRank(a.severity) - severityRank(b.severity);
  if (severity !== 0) return severity;
  return (a.due ?? "9999-12-31").localeCompare(b.due ?? "9999-12-31");
}

function severityRank(severity?: RecordSeverity): number {
  if (severity === "high") return 0;
  if (severity === "low") return 2;
  return 1;
}

function trackExtent(issue: HeldIssue, today: ISODate): number {
  if (issue.late) return 100;
  if (!issue.due || issue.due <= issue.opened) return 40;
  const span = diffDays(issue.opened, issue.due);
  if (span <= 0) return 40;
  const gone = diffDays(issue.opened, today);
  return Math.max(8, Math.min(90, Math.round((gone / span) * 100)));
}

function asOf(weekStart: ISODate, today: ISODate): ISODate {
  const end = addDays(weekStart, 6);
  return end < today ? end : today;
}

function dayOf(value?: string): ISODate | undefined {
  if (!value) return undefined;
  const day = value.slice(0, 10);
  return isISODate(day) ? day : undefined;
}

function attachmentNames(record: ProjectRecord): string[] | undefined {
  const names = record.attachments.map((item) => item.title).filter(Boolean);
  return names.length > 0 ? names : undefined;
}

function closeNote(record: ProjectRecord): string | undefined {
  const resolution = record.sections.resolution?.trim();
  if (resolution) return resolution;
  const comment = [...record.comments].reverse().find((item) => !item.deleted && item.body.trim());
  return comment?.body.trim();
}
